import { Fragment, useEffect, useMemo, useState } from "react";
import { ArrowLeft, AlertCircle, Calendar, CheckCircle2, ChevronRight, Layers, Loader2, MapPin, Send } from "lucide-react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";

import Layout from "../../components/Layout";
import api, { formatApiError } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from "../../components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "../../components/ui/alert-dialog";

const CRITERIA = [
  { key: "tecnica", label: "Tecnica", short: "Tec." },
  { key: "coreografia", label: "Coreografia", short: "Cor." },
  { key: "musicalita", label: "Musicalità", short: "Mus." },
];

const DANCE_LABELS = {
  samba: "Samba",
  rumba: "Rumba",
  cha_cha_cha: "Cha Cha Cha",
  jive: "Jive",
  paso_doble: "Paso Doble",
};

function ScoreCell({ value, onClick, disabled, testId, compact }) {
  const hSizes = compact ? "h-12 sm:h-12 text-2xl" : "h-16 sm:h-14 text-3xl sm:text-2xl";
  if (value != null) {
    return (
      <button
        type="button"
        onClick={onClick}
        disabled={disabled}
        data-testid={testId}
        className={`${hSizes} w-full rounded-lg font-display font-bold tabular-nums transition active:scale-95
          ${disabled
            ? "bg-emerald-600/85 text-white cursor-default"
            : "bg-emerald-600 text-white hover:bg-emerald-700 sm:hover:scale-[1.02] focus:ring-4 focus:ring-emerald-200"
          }`}
      >
        {value}
      </button>
    );
  }
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      data-testid={testId}
      className={`${hSizes} w-full rounded-lg border-2 border-sky-300 bg-sky-100 text-sky-800 font-medium transition flex items-center justify-center gap-1.5 active:scale-95
        ${disabled ? "opacity-60 cursor-default" : "hover:bg-sky-200 hover:border-sky-400 focus:ring-4 focus:ring-sky-200"}`}
    >
      <AlertCircle className="h-5 w-5 text-amber-500 fill-amber-100 flex-shrink-0" strokeWidth={2.5} />
      <span className="text-sm">Vota</span>
    </button>
  );
}

export default function JudgeCompetition() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [picker, setPicker] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [finalizing, setFinalizing] = useState(false);
  const [completingBatch, setCompletingBatch] = useState(false);
  const [selectedBatchId, setSelectedBatchId] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get(`/competitions/${id}/my-scores`);
      setData(data);
    } catch (err) {
      toast.error(formatApiError(err));
      navigate("/responsabile/dance-analysis", { replace: true });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const isLatin = data?.type === "latin";
  const finalized = data?.finalized;

  const onPick = async (score) => {
    if (!picker) return;
    setSubmitting(true);
    try {
      const payload = { [picker.criterion]: score };
      if (isLatin) payload.dance = picker.dance;
      await api.put(`/competitions/${id}/scores/${picker.athleteId}`, payload);
      setData((prev) => ({
        ...prev,
        athletes: prev.athletes.map((a) => {
          if (a.id !== picker.athleteId) return a;
          if (isLatin) {
            const updated = { ...(a.scores_by_dance || {}) };
            updated[picker.dance] = { ...(updated[picker.dance] || {}), [picker.criterion]: score };
            return { ...a, scores_by_dance: updated };
          }
          return { ...a, scores: { ...a.scores, [picker.criterion]: score } };
        }),
      }));
      setPicker(null);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  const onFinalize = async () => {
    setFinalizing(true);
    try {
      await api.post(`/competitions/${id}/finalize`);
      toast.success("Votazione inviata");
      setSelectedBatchId(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setFinalizing(false);
    }
  };

  const onCompleteBatch = async () => {
    if (!selectedBatchId) return;
    setCompletingBatch(true);
    try {
      await api.post(`/competitions/${id}/batches/${selectedBatchId}/complete`);
      toast.success("Batteria completata");
      setSelectedBatchId(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setCompletingBatch(false);
    }
  };

  const stats = useMemo(() => {
    if (!data) return { voted: 0, total: 0 };
    let voted = 0;
    let total = 0;
    if (isLatin) {
      const dances = data.dances || [];
      data.athletes.forEach((a) => {
        dances.forEach((d) => {
          CRITERIA.forEach(({ key }) => {
            total += 1;
            if ((a.scores_by_dance?.[d] || {})[key] != null) voted += 1;
          });
        });
      });
    } else {
      data.athletes.forEach((a) => {
        CRITERIA.forEach(({ key }) => {
          total += 1;
          if (a.scores[key] != null) voted += 1;
        });
      });
    }
    return { voted, total };
  }, [data, isLatin]);

  const completedBatches = useMemo(() => new Set(data?.completed_batches || []), [data]);

  if (loading || !data) {
    return (
      <Layout>
        <div className="p-10 text-center text-slate-500 text-sm">Caricamento…</div>
      </Layout>
    );
  }

  const FinalizeButton = ({ testId, fullWidth, label }) => (
    <AlertDialog>
      <AlertDialogTrigger asChild>
        <Button
          size="lg"
          disabled={finalized || finalizing}
          className={`bg-rose-600 hover:bg-rose-700 text-white font-semibold h-12 px-6 ${fullWidth ? "w-full sm:w-auto" : ""}`}
          data-testid={testId}
        >
          {finalizing ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Send className="h-4 w-4 mr-2" />}
          {finalized ? "VOTAZIONE INVIATA" : (label || "TERMINA VOTAZIONE")}
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent className="max-w-[95vw] sm:max-w-md">
        <AlertDialogHeader>
          <AlertDialogTitle>{isLatin ? "Termina competizione?" : "Termina votazione?"}</AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div className="text-sm text-slate-600 space-y-3">
              <p>Sei certo di voler terminare la votazione ed inviare i giudizi espressi?</p>
              <p className="text-rose-700 font-semibold">Una volta inviati non potrai più modificare!</p>
              {stats.voted < stats.total && (
                <p className="mt-3 text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm">
                  Attenzione: {stats.total - stats.voted} casell{stats.total - stats.voted === 1 ? "a" : "e"} non votat{stats.total - stats.voted === 1 ? "a" : "e"} verrann{stats.total - stats.voted === 1 ? "o" : "o"} impostat{stats.total - stats.voted === 1 ? "a" : "e"} a <strong>1</strong> automaticamente.
                </p>
              )}
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter className="flex-col sm:flex-row gap-2">
          <AlertDialogCancel className="w-full sm:w-auto" data-testid={`${testId}-cancel`}>Annulla</AlertDialogCancel>
          <AlertDialogAction
            onClick={onFinalize}
            className="bg-rose-600 hover:bg-rose-700 w-full sm:w-auto"
            data-testid={`${testId}-confirm`}
          >
            OK, invia
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );

  const CompleteBatchButton = ({ testId, fullWidth }) => (
    <Button
      size="lg"
      disabled={completingBatch || finalized}
      onClick={onCompleteBatch}
      className={`bg-emerald-700 hover:bg-emerald-800 text-white font-semibold h-12 px-6 ${fullWidth ? "w-full sm:w-auto" : ""}`}
      data-testid={testId}
    >
      {completingBatch ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <CheckCircle2 className="h-4 w-4 mr-2" />}
      COMPLETA BATTERIA
    </Button>
  );

  // ====================================================
  // LATIN — Batches list view (when no batch is selected)
  // ====================================================
  if (isLatin && !selectedBatchId) {
    const batches = data.batches || [];
    return (
      <Layout>
        <Link to="/responsabile/dance-analysis" className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-slate-900 mb-4 sm:mb-6" data-testid="back-to-judging">
          <ArrowLeft className="h-4 w-4" /> Torna a Dance Analysis
        </Link>
        <div className="bg-white rounded-2xl ring-1 ring-slate-200 p-4 sm:p-8 mb-4 sm:mb-6">
          <p className="text-[10px] sm:text-xs uppercase tracking-[0.25em] text-blue-700 font-bold">Pannello Giudicante · Latin Style</p>
          <h1 className="font-display text-xl sm:text-3xl font-bold text-slate-900 tracking-tight mt-1.5 break-words">{data.name}</h1>
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs sm:text-sm text-slate-600 mt-2">
            <span className="inline-flex items-center gap-1.5"><MapPin className="h-3.5 w-3.5" />{data.location}</span>
            <span className="inline-flex items-center gap-1.5"><Calendar className="h-3.5 w-3.5" />{new Date(data.date).toLocaleDateString("it-IT", { day: "2-digit", month: "long", year: "numeric" })}</span>
            <span className="inline-flex items-center gap-1.5"><Layers className="h-3.5 w-3.5" />{batches.length} batter{batches.length === 1 ? "ia" : "ie"}</span>
          </div>
          {data.dances?.length > 0 && (
            <div className="mt-3 flex flex-wrap gap-2">
              {data.dances.map((d) => (
                <Badge key={d} className="bg-fuchsia-100 text-fuchsia-800 hover:bg-fuchsia-100">{DANCE_LABELS[d] || d}</Badge>
              ))}
            </div>
          )}
        </div>

        <h2 className="font-display text-lg font-semibold text-slate-900 mb-3">Seleziona la batteria da giudicare</h2>
        <div className="space-y-3 mb-6" data-testid="batches-list">
          {batches.map((b) => {
            const done = completedBatches.has(b.id);
            return (
              <button
                key={b.id}
                onClick={() => setSelectedBatchId(b.id)}
                className="w-full bg-white rounded-2xl ring-1 ring-slate-200 hover:ring-indigo-300 hover:shadow-md transition px-5 py-4 flex items-center gap-4 text-left"
                data-testid={`select-batch-${b.id}`}
              >
                <div className="h-11 w-11 rounded-xl bg-indigo-50 text-indigo-700 flex items-center justify-center">
                  <Layers className="h-5 w-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-display text-lg font-semibold text-slate-900">{b.name}</h3>
                  <p className="text-xs text-slate-500 mt-0.5">{b.athlete_ids?.length || 0} atlet{(b.athlete_ids?.length || 0) === 1 ? "a" : "i"}</p>
                </div>
                {done ? (
                  <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100 gap-1"><CheckCircle2 className="h-3.5 w-3.5" /> Completata</Badge>
                ) : (
                  <Badge variant="outline" className="border-slate-300 text-slate-600">Da giudicare</Badge>
                )}
                <ChevronRight className="h-5 w-5 text-slate-400" />
              </button>
            );
          })}
        </div>

        <div className="bg-white rounded-2xl ring-1 ring-slate-200 p-4 sm:p-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <p className="font-display text-base sm:text-lg font-semibold text-slate-900">
              {finalized ? "Hai concluso la votazione" : "Hai giudicato tutte le batterie?"}
            </p>
            <p className="text-xs sm:text-sm text-slate-500 mt-1">
              {finalized
                ? "I tuoi voti sono stati inviati e non sono più modificabili."
                : "Le caselle non votate verranno impostate a 1 al momento dell'invio."}
            </p>
          </div>
          {finalized ? (
            <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100 px-4 py-2 text-sm gap-1.5"><CheckCircle2 className="h-4 w-4" /> Inviata</Badge>
          ) : (
            <FinalizeButton testId="finalize-latin" fullWidth label="TERMINA COMPETIZIONE" />
          )}
        </div>

        <Dialog open={!!picker} onOpenChange={(o) => { if (!o) setPicker(null); }}>
          <DialogContent className="max-w-[95vw] sm:max-w-md p-5 sm:p-6">
            <DialogHeader>
              <DialogTitle className="font-display text-base sm:text-lg break-words">{picker?.title}</DialogTitle>
              <DialogDescription className="text-xs sm:text-sm">Seleziona il voto da 1 a 10.</DialogDescription>
            </DialogHeader>
            <div className="grid grid-cols-5 gap-2 sm:gap-2.5 py-2">
              {[1,2,3,4,5,6,7,8,9,10].map((n) => (
                <button key={n} onClick={() => onPick(n)} disabled={submitting}
                  className="h-14 rounded-lg bg-slate-50 hover:bg-blue-700 hover:text-white border-2 border-slate-200 hover:border-blue-700 font-display text-2xl font-bold text-slate-900 transition focus:ring-4 focus:ring-blue-200 active:scale-95"
                  data-testid={`score-pick-${n}`}
                >{n}</button>
              ))}
            </div>
          </DialogContent>
        </Dialog>
      </Layout>
    );
  }

  // ====================================================
  // LATIN — Batch detail view (selectedBatchId set)
  // ====================================================
  if (isLatin && selectedBatchId) {
    const batch = (data.batches || []).find((b) => b.id === selectedBatchId);
    const batchAthletes = batch
      ? batch.athlete_ids.map((aid) => data.athletes.find((a) => a.id === aid)).filter(Boolean)
      : [];
    const dances = data.dances || [];

    return (
      <Layout>
        <button onClick={() => setSelectedBatchId(null)} className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-slate-900 mb-4" data-testid="back-to-batches">
          <ArrowLeft className="h-4 w-4" /> Torna alle batterie
        </button>

        <div className="bg-white rounded-2xl ring-1 ring-slate-200 p-4 sm:p-6 mb-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div>
            <p className="text-[10px] uppercase tracking-[0.25em] text-indigo-700 font-bold">Batteria in giudizio</p>
            <h1 className="font-display text-xl sm:text-2xl font-bold text-slate-900 mt-1">{batch?.name}</h1>
            <p className="text-xs text-slate-500 mt-1">{batchAthletes.length} atleti · {dances.length} ball{dances.length === 1 ? "o" : "i"}</p>
          </div>
          <CompleteBatchButton testId="complete-batch-top" fullWidth />
        </div>

        {/* Horizontal grid: rows = athletes, columns = dances × (T/C/M). Number column sticky. */}
        <div className="bg-white rounded-2xl ring-1 ring-slate-200 overflow-hidden mb-6" data-testid="latin-judge-grid">
          <div className="overflow-x-auto">
            <table className="min-w-max border-separate" style={{ borderSpacing: 0 }}>
              <thead>
                <tr>
                  <th
                    className="sticky left-0 z-20 bg-slate-50 px-3 py-2 w-[72px] min-w-[72px] border-b border-slate-200"
                    rowSpan={2}
                  >
                    <span className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">N°</span>
                  </th>
                  {dances.map((d, i) => (
                    <th
                      key={d}
                      colSpan={3}
                      className={`px-2 py-2 text-fuchsia-700 text-xs uppercase tracking-wider font-bold text-center bg-fuchsia-50/60 border-b border-slate-200 ${i > 0 ? "border-l-2 border-l-slate-200" : "border-l border-l-slate-200"}`}
                    >
                      {DANCE_LABELS[d] || d}
                    </th>
                  ))}
                </tr>
                <tr>
                  {dances.map((d, i) => (
                    <Fragment key={d}>
                      {CRITERIA.map(({ key, short }, ci) => (
                        <th
                          key={key}
                          className={`text-[10px] uppercase tracking-wider text-slate-500 font-semibold px-2 py-2 bg-slate-50 border-b border-slate-200 w-[88px] min-w-[88px] ${ci === 0 ? (i > 0 ? "border-l-2 border-l-slate-200" : "border-l border-l-slate-200") : ""}`}
                        >
                          {short}
                        </th>
                      ))}
                    </Fragment>
                  ))}
                </tr>
              </thead>
              <tbody>
                {batchAthletes.map((a, ri) => (
                  <tr key={a.id} data-testid={`judge-athlete-${a.id}`}>
                    <td
                      className={`sticky left-0 z-10 bg-white px-2 py-2 w-[72px] min-w-[72px] border-r border-slate-200 ${ri > 0 ? "border-t border-slate-100" : ""}`}
                    >
                      <div className="h-12 w-full rounded-lg bg-blue-50 text-blue-700 flex items-center justify-center font-mono font-bold text-base sm:text-lg">
                        {a.number}
                      </div>
                    </td>
                    {dances.map((d, di) => (
                      <Fragment key={d}>
                        {CRITERIA.map(({ key, label }, ci) => (
                          <td
                            key={key}
                            className={`px-1.5 py-2 align-middle ${ri > 0 ? "border-t border-slate-100" : ""} ${ci === 0 ? (di > 0 ? "border-l-2 border-l-slate-200" : "border-l border-l-slate-200") : ""}`}
                          >
                            <ScoreCell
                              compact
                              value={(a.scores_by_dance?.[d] || {})[key]}
                              disabled={finalized}
                              onClick={() => setPicker({
                                athleteId: a.id,
                                criterion: key,
                                dance: d,
                                title: `N° ${a.number} · ${DANCE_LABELS[d]} · ${label}`,
                              })}
                              testId={`score-${a.id}-${d}-${key}`}
                            />
                          </td>
                        ))}
                      </Fragment>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {batchAthletes.length === 0 && (
            <div className="p-8 text-center text-sm text-slate-500">Batteria vuota.</div>
          )}
        </div>

        <div className="bg-white rounded-2xl ring-1 ring-slate-200 p-4 sm:p-6 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <p className="text-sm text-slate-600">Quando hai finito i voti di tutti gli atleti di questa batteria, conferma:</p>
          <CompleteBatchButton testId="complete-batch-bottom" fullWidth />
        </div>

        <Dialog open={!!picker} onOpenChange={(o) => { if (!o) setPicker(null); }}>
          <DialogContent className="max-w-[95vw] sm:max-w-md p-5 sm:p-6">
            <DialogHeader>
              <DialogTitle className="font-display text-base sm:text-lg break-words">{picker?.title}</DialogTitle>
              <DialogDescription className="text-xs sm:text-sm">Seleziona il voto da 1 a 10.</DialogDescription>
            </DialogHeader>
            <div className="grid grid-cols-5 gap-2 sm:gap-2.5 py-2">
              {[1,2,3,4,5,6,7,8,9,10].map((n) => (
                <button key={n} onClick={() => onPick(n)} disabled={submitting}
                  className="h-14 rounded-lg bg-slate-50 hover:bg-blue-700 hover:text-white border-2 border-slate-200 hover:border-blue-700 font-display text-2xl font-bold text-slate-900 transition focus:ring-4 focus:ring-blue-200 active:scale-95"
                  data-testid={`score-pick-${n}`}
                >{n}</button>
              ))}
            </div>
          </DialogContent>
        </Dialog>
      </Layout>
    );
  }

  // ====================================================
  // SINGOLO (existing flow) — unchanged below
  // ====================================================
  return (
    <Layout>
      <Link to="/responsabile/dance-analysis" className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-slate-900 mb-4 sm:mb-6" data-testid="back-to-judging">
        <ArrowLeft className="h-4 w-4" /> Torna a Dance Analysis
      </Link>

      <div className="bg-white rounded-2xl ring-1 ring-slate-200 p-4 sm:p-8 mb-4 sm:mb-6">
        <div className="flex flex-col gap-4">
          <div>
            <p className="text-[10px] sm:text-xs uppercase tracking-[0.25em] text-blue-700 font-bold">Pannello Giudicante</p>
            <h1 className="font-display text-xl sm:text-3xl font-bold text-slate-900 tracking-tight mt-1.5 break-words">{data.name}</h1>
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs sm:text-sm text-slate-600 mt-2">
              <span className="inline-flex items-center gap-1.5"><MapPin className="h-3.5 w-3.5 flex-shrink-0" />{data.location}</span>
              <span className="inline-flex items-center gap-1.5"><Calendar className="h-3.5 w-3.5 flex-shrink-0" />{new Date(data.date).toLocaleDateString("it-IT", { day: "2-digit", month: "long", year: "numeric" })}</span>
              <span className="inline-flex items-center gap-1.5 font-semibold">
                <span className={stats.voted === stats.total ? "text-emerald-700" : "text-amber-700"}>
                  {stats.voted}/{stats.total} voti
                </span>
              </span>
            </div>
          </div>
          <div>
            {finalized ? (
              <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100 px-3 py-1.5 text-sm gap-1.5">
                <CheckCircle2 className="h-4 w-4" /> Votazione conclusa
              </Badge>
            ) : (
              <FinalizeButton testId="finalize-top" fullWidth />
            )}
          </div>
        </div>
      </div>

      <div className="space-y-3 sm:space-y-4">
        {data.athletes.map((a) => (
          <div key={a.id} className="bg-white rounded-2xl ring-1 ring-slate-200 p-4 sm:p-6" data-testid={`judge-athlete-${a.id}`}>
            <div className="flex items-center gap-3 sm:gap-4 mb-3 sm:mb-4">
              <div className="flex-shrink-0 h-12 w-14 sm:h-14 sm:w-16 rounded-lg bg-blue-50 text-blue-700 flex items-center justify-center font-mono font-bold text-lg sm:text-xl">
                {a.number}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-display font-semibold text-slate-900 text-base sm:text-lg break-words">{a.name}</div>
                <div className="text-[10px] sm:text-xs text-slate-500 uppercase tracking-wider mt-0.5">{a.type}</div>
              </div>
            </div>

            <div className="grid grid-cols-3 gap-2 sm:gap-3">
              {CRITERIA.map(({ key, label }) => (
                <div key={key} className="flex flex-col gap-1.5">
                  <span className="text-[10px] sm:text-xs uppercase tracking-wider text-slate-500 font-semibold text-center">{label}</span>
                  <ScoreCell
                    value={a.scores[key]}
                    disabled={finalized}
                    onClick={() => setPicker({ athleteId: a.id, criterion: key, title: `${a.name} — ${label}` })}
                    testId={`score-${a.id}-${key}`}
                  />
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      <div className="mt-6 sm:mt-8 bg-white rounded-2xl ring-1 ring-slate-200 p-4 sm:p-6">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
          <div className="min-w-0">
            <p className="font-display text-base sm:text-lg font-semibold text-slate-900">
              {finalized ? "Hai concluso la votazione" : "Hai finito di votare?"}
            </p>
            <p className="text-xs sm:text-sm text-slate-500 mt-1">
              {finalized
                ? "I tuoi voti sono stati inviati e non sono più modificabili."
                : "Le caselle non votate verranno impostate a 1 al momento dell'invio."}
            </p>
          </div>
          {finalized ? (
            <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100 px-4 py-2 text-sm gap-1.5 self-start sm:self-auto">
              <CheckCircle2 className="h-4 w-4" /> Votazione inviata
            </Badge>
          ) : (
            <FinalizeButton testId="finalize-bottom" fullWidth />
          )}
        </div>
      </div>

      <Dialog open={!!picker} onOpenChange={(o) => { if (!o) setPicker(null); }}>
        <DialogContent className="max-w-[95vw] sm:max-w-md p-5 sm:p-6">
          <DialogHeader>
            <DialogTitle className="font-display text-base sm:text-lg break-words">{picker?.title}</DialogTitle>
            <DialogDescription className="text-xs sm:text-sm">Seleziona il voto da 1 a 10.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-5 gap-2 sm:gap-2.5 py-2">
            {[1,2,3,4,5,6,7,8,9,10].map((n) => (
              <button key={n} onClick={() => onPick(n)} disabled={submitting}
                className="h-14 sm:h-14 rounded-lg bg-slate-50 hover:bg-blue-700 hover:text-white border-2 border-slate-200 hover:border-blue-700 font-display text-2xl font-bold text-slate-900 transition focus:ring-4 focus:ring-blue-200 active:scale-95"
                data-testid={`score-pick-${n}`}
              >{n}</button>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
